import { createNamespacedHelpers } from 'vuex';
import { DefineActions, DefineGetters, DefineMutations } from 'vuex-type-helper';
import { Vehicles, Getters, Mutations, Actions, Vehicle, WorkshopSummary } from '@/models/Vehicle.model';
import { Type } from '@/models/App.model';
import dayjs from 'dayjs';

// Inital state
const state: Vehicles = {
  vehicleTypes: [],
  activeVehicles: [],
  disposedVehicles: [],
  workshopSummary: [],
};

// Gets the values of our state usually called from components
const getters: DefineGetters<Getters, Vehicles> = {
  // YAY! no complex nested objects to worry about here - https://github.com/vuejs/vue/issues/4292 complex nested objects are not reactive
  emptyVehicle: (state) => {
    return {
      id: 0,
      vehicleTypeId: 0,
      vehicleType: '',
      code: '',
      make: '',
      model: '',
      yearModel: '',
      vin: '',
      registrationNumber: '',
      odometer: 0,
      roadworthyDate: '',
      licenseEndDate: '',
      wheelAlignmentIntervalKm: 0,
      nextWheelAlignmentOdo: 0,
      serviceIntervalKm: 0,
      nextServiceOdo: 0,
      vehicleStatusId: 0,
      vehicleStatus: '',
      suspended: false,
      canDispose: false,
    };
  },

  vehicleTypes: (state) => state.vehicleTypes,
  activeVehicles: (state) => state.activeVehicles,
  disposedVehicles: (state) => state.disposedVehicles,
  workshopSummary: (state) => state.workshopSummary,

  vehicleTypeIconByTypeId: (state) =>  (typeId: number): string | undefined => {
    const foundIndex = state.vehicleTypes.findIndex((item: Type) => item.id === typeId);

    if (foundIndex >= 0) {
      return state.vehicleTypes[foundIndex].icon;
    } else {
      return undefined;
    }
  },

  vehiclesByType: (state) => (type: string): Vehicle[] => {
    return state.activeVehicles.filter((item: Vehicle) => item.vehicleType === type);
  },

  offloadedVehiclesByType: (state) => (type: string): Vehicle[] => {
    return state.activeVehicles.filter((item: Vehicle) => item.vehicleType === type && item.vehicleStatusId === 45);
  },

  vehicleById: (state) => (id: number): Vehicle | undefined => {
    return state.activeVehicles.find((item: Vehicle) => item.id === id);
  },

  activeOrDisposedVehicleById: (state) => (id: number): Vehicle | undefined => {
    return (state.activeVehicles.find((item: Vehicle) => item.id === id) || state.disposedVehicles.find((item: Vehicle) => item.id === id));
  },

  showWarning: (state) => (type: string, item: Vehicle | WorkshopSummary): string => {
    let style: string = '';

    if (item) {      
      switch (type) {
        case 'service':
          if (item.odometer > item.nextServiceOdo) {
            style = 'overdue';
          } else if (item.odometer + 5000 > item.nextServiceOdo) {
            style = 'service-due';
          }
          break;

        case 'wheel':
          if (item.odometer > item.nextWheelAlignmentOdo) {
            style = 'overdue';
          } else if (item.odometer + 5000 > item.nextWheelAlignmentOdo) {
            style = 'service-due';
          }
          break;

        case 'license':
          const licenseEndDate = dayjs(item.licenseEndDate);
          if (dayjs().isAfter(licenseEndDate)) {
            style = 'overdue';
          } else if (dayjs().isAfter(licenseEndDate.subtract(30, 'days'))) {
            style = 'service-due';
          }
          break;
      }
    }
    
    return style;
  }
};

// TODO: Change mutation names to uppercase ( Standards )
// Sets values or changes to state. Must be called via an action.
const mutations: DefineMutations<Mutations, Vehicles> = {
  setVehicleTypes(state, { vehicleTypes }) {
    state.vehicleTypes = vehicleTypes;
  },

  setActiveVehicles(state, { vehicles }) {
    state.activeVehicles = vehicles;
  },  

  setDisposedVehicles(state, { vehicles }) {
    state.disposedVehicles = vehicles;
  },
  
  setWorkshopSummary(state, { workshopSummary }) {
    state.workshopSummary = workshopSummary;
  },
};

// Async functions that will contain the logic / service calls and commit mutations to state
const actions: DefineActions<Actions, Vehicles, Mutations, Getters> = {
  setVehicleTypes({ commit }, payload) {
    commit('setVehicleTypes', payload);
  },

  setActiveVehicles({ commit }, payload) {
    commit('setActiveVehicles', payload);
  }, 
  
  setDisposedVehicles({ commit }, payload) {
    commit('setDisposedVehicles', payload);
  },

  setWorkshopSummary({ commit }, payload) {
    commit('setWorkshopSummary', payload);
  },
};

export const {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} = createNamespacedHelpers<Vehicles, Getters, Mutations, Actions>('VehiclesModule');

export const VehiclesModule = {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
};
